import { PLAY_TEST_PAIR_SLOTS, type ExportPackageKind, type ExportPreviewResource } from '@studio/shared';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../../shell/api.js';
import { EmptyState, Failure } from '../../shell/EmptyState.js';
import { Link } from '../../shell/router.js';
import { useReview } from './useReview.js';

/**
 * The Packing desk: exactly what will be delivered, before anything is downloaded.
 *
 * There is no "export the latest" here. A package holds what was explicitly selected -
 * the Play Test Pair's slots, or the Shortlist - and a Candidate that fails its
 * Technical Check is named as left out rather than quietly dropped or allowed to block
 * the rest. Downloading changes nothing in Play Console.
 */
export function PackingDesk({ experimentId }: { experimentId: string }): ReactNode {
  const open = useReview(experimentId);
  const [chosen, setChosen] = useState<ExportPackageKind | null>(null);
  const [preview, setPreview] = useState<ExportPreviewResource | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const review = open.value;
  const reload = open.reload;

  /**
   * What a full Play Test Pair means.
   *
   * Both slots filled is the operator having said, explicitly, which two Candidates go
   * against the Control. R122 is worded as the alternative - all kept Candidates
   * `instead of` a Play Test Pair - so the pair leads, and the alternative is a press.
   * Defaulting the other way round packs something else than what the slots say, and
   * the archive is only opened later, somewhere else.
   */
  const pairFull = review !== null && review.pair.eligible && review.pair.slots.length === PLAY_TEST_PAIR_SLOTS;
  const kind: ExportPackageKind = chosen ?? (pairFull ? 'playTestPair' : 'allKept');

  const loadPreview = useCallback(() => {
    // Nothing is described until the slots are known: a preview asked for before that
    // would be a description of a different package than the one this desk will pack.
    if (review === null) return;
    api
      .readExportPreview(experimentId, kind)
      .then(setPreview)
      .catch((caught: unknown) => setFailure(caught instanceof ApiError ? caught.message : 'Пакет не удалось описать.'));
  }, [experimentId, kind, review]);

  useEffect(loadPreview, [loadPreview, review]);

  if (open.error !== null) {
    return (
      <section className="page">
        <Failure message={open.error} onRetry={reload} />
      </section>
    );
  }
  if (review === null) return <section className="page" aria-busy="true" />;

  const prepare = async (): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await api.requestExportPackage(experimentId, kind);
      reload();
    } catch (caught) {
      setFailure(caught instanceof ApiError ? caught.message : 'Пакет не удалось запустить.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="page packing-desk">
      <div className="page-head">
        <div>
          <p className="eyebrow">Packing desk · подготовка Export</p>
          <h1>Каждый файл на своё место.</h1>
          <p className="note">Ничего не публикуется автоматически. Загрузите эти файлы вручную в Play Console.</p>
        </div>
        <Link to={`/experiments/${experimentId}/compare`} className="secondary-link">
          К Contact sheet
        </Link>
      </div>

      <div className="row-actions" role="group" aria-label="Что упаковать">
        <button
          type="button"
          aria-pressed={kind === 'playTestPair'}
          disabled={!review.pair.eligible}
          onClick={() => setChosen('playTestPair')}
        >
          {`Play Test Pair · ${review.pair.slots.length}/${PLAY_TEST_PAIR_SLOTS} слотов`}
        </button>
        <button type="button" aria-pressed={kind === 'allKept'} onClick={() => setChosen('allKept')}>
          Export всех Kept Candidate вместо этого
        </button>
      </div>
      {review.pair.eligible ? null : <p className="note">{review.pair.eligibilityNote}</p>}

      {review.pair.slots.length === 0 ? null : (
        <ul className="pair-slots" aria-label="Слоты Play Test Pair">
          {review.pair.slots.map((slot) => (
            <li key={slot.slotIndex}>
              <span>{`Play variant ${slot.slotIndex} · ${slot.label}`}</span>
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  void api.clearPlayTestPairSlot(experimentId, slot.slotIndex).then(reload);
                }}
              >
                {`Очистить Play variant ${slot.slotIndex}`}
              </button>
            </li>
          ))}
        </ul>
      )}

      {failure === null ? null : <Failure message={failure} />}

      {preview === null ? null : (
        <section aria-label="Содержимое пакета">
          <h2>{preview.rootFolder}</h2>
          {preview.entries.length === 0 ? (
            <EmptyState
              title="Упаковывать нечего."
              note="Выберите Candidate для Export: Keep на Contact sheet или поставьте их в два слота Play variant. Автоподстановки новейшего результата нет."
            />
          ) : (
            <ul className="file-list">
              {preview.entries.map((entry) => (
                <li key={entry.path}>{`${entry.path} — ${entry.candidateLabel}`}</li>
              ))}
              <li>UPLOAD_ORDER.txt</li>
              <li>PLAY_CONSOLE_CHECKLIST.txt</li>
              <li>manifest.json</li>
            </ul>
          )}
          {preview.omitted.length === 0 ? null : (
            <ul className="omitted" aria-label="Не вошло в этот пакет">
              {preview.omitted.map((entry) => (
                <li key={entry.candidateId}>{`${entry.label} — ${entry.reason}`}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      <div className="row-actions">
        <button type="button" className="primary" disabled={busy || preview?.ready !== true} onClick={() => void prepare()}>
          {busy ? 'Упаковываю…' : 'Подготовить пакет'}
        </button>
      </div>

      <section aria-label="Export Packages">
        <h2>Пакеты</h2>
        {review.packages.length === 0 ? (
          <EmptyState
            title="Пакет ещё не собран."
            note="Подготовка заново проверяет каждый выбранный файл по текущим правилам Google Play, затем пишет один ZIP, который вы скачиваете вручную."
          />
        ) : (
          <ul className="package-list">
            {review.packages.map((entry) => (
              <li key={entry.id}>
                <a href={entry.downloadPath} download>
                  {`Скачать ${entry.fileName}`}
                </a>
                <span className="note">{`${entry.itemCount} файлов · правила ${entry.rulesVersion} · ${entry.createdAt}`}</span>
                {entry.omitted.length === 0 ? null : (
                  <span className="note">{`Не вошло: ${entry.omitted.map((omission) => omission.label).join(', ')}`}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}
